/**
 * Place lookup for the "Locked" location-privacy mode.
 *
 * Locked is the one mode the server publishes VERBATIM — no jitter, no
 * quantisation (server/location/locationPrivacyService.js). Whatever a user
 * picks here becomes the exact point every other collector sees, so the result
 * types are deliberately limited to settlements and public places. `address` is
 * excluded: an address-level pin would publish someone's doorstep at full
 * precision, which is the one thing every other mode exists to prevent.
 *
 * Its own module rather than inline in the panel so the request shape can be
 * asserted without rendering MUI, and so the credential check has one home.
 */

import { isRoamingEnabled } from '../../utils/runtimeFlags';

// Towns, neighbourhoods, and public places (a card shop, a mall, a station).
// Deliberately no `address`.
const PLACE_TYPES = 'place,locality,neighborhood,poi';

const GEOCODER = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

// The beta only serves the launch area: server/location/discoveryPolicy.js
// aliases ISRAEL_BOUNDS as LAUNCH_AREA_BOUNDS and its roaming gate fails CLOSED.
// Locked publishes the chosen point VERBATIM, so an unconstrained picker lets
// someone lock themselves to a place discovery will never query and vanish from
// every collector's map without being told they have. Unconstrained, the
// geocoder also ranks on fuzzy string match alone: "tel aviv" returned Avivim,
// Avio in Italy and Lviv in Ukraine, because nothing said where the searcher is.
const LAUNCH_COUNTRY = 'IL';

const RESULT_LIMIT = 5;

/** The shortest query worth spending a geocoding request on. */
export const MIN_QUERY_LENGTH = 2;

/**
 * Decimal places of a degree kept in the `proximity` sent to Mapbox.
 *
 * The caller hands over the searcher's real GPS fix (MapPage's
 * originCoordinates, through LocationPrivacySettings). Ranking places "near
 * you" needs a neighbourhood, not a doorstep, so Mapbox gets two decimal
 * places: 0.01 degree is about 1.1 km north-south and less east-west. The
 * Privacy Policy says "to about 1 kilometre"; server/__tests__/
 * legalPolicyVersion.test.js reads this number, so changing it fails that
 * test until the policy says what the new value does.
 */
export const PROXIMITY_DECIMALS = 2;

const PROXIMITY_SCALE = 10 ** PROXIMITY_DECIMALS;

/** A coordinate rounded for Mapbox. `+ 0` turns a -0 into 0, so no "-0" is sent. */
const coarse = (n) => String(Math.round(n * PROXIMITY_SCALE) / PROXIMITY_SCALE + 0);

// Read at call time rather than at module load. The bundler substitutes a
// literal here either way, and reading lazily keeps the value observable to a
// test that has just set it.
const token = () => process.env.REACT_APP_MAPBOX_TOKEN || '';

/**
 * Can places be looked up at all?
 *
 * False when the build carries no map credentials. The panel has to say so
 * plainly instead of rendering a search box that can only ever come back empty
 * — an empty box reads as "no such place", which is a different claim.
 */
export const isPlaceSearchAvailable = () => !!token();

/**
 * Search for a place by name.
 *
 * Pass `proximity` as the searcher's `[lng, lat]` to rank nearby results
 * first; without it the geocoder ranks on string similarity alone. Only a
 * rounded copy of it is sent (PROXIMITY_DECIMALS).
 *
 * Resolves to `[{ id, name, coordinates: [lng, lat] }]`. Rejects when the
 * lookup itself fails, so the caller can distinguish "nothing matched" from
 * "we could not ask" — the map's roaming search collapses both into an empty
 * list, and here that difference decides whether the user is stuck.
 */
export const searchPlaces = async (query, { proximity } = {}) => {
  const trimmed = (query || '').trim();
  if (trimmed.length < MIN_QUERY_LENGTH || !isPlaceSearchAvailable()) return [];

  // Commas stay unescaped: that is the shape Mapbox documents, and the request
  // is asserted as a string rather than parsed.
  const usable = (p) =>
    Array.isArray(p) && p.length === 2 && p.every((n) => Number.isFinite(n));

  const response = await fetch(
    `${GEOCODER}/${encodeURIComponent(trimmed)}.json` +
      `?access_token=${token()}&types=${PLACE_TYPES}&limit=${RESULT_LIMIT}` +
      (isRoamingEnabled() ? '' : `&country=${LAUNCH_COUNTRY}`) +
      (usable(proximity) ? `&proximity=${coarse(proximity[0])},${coarse(proximity[1])}` : '')
  );
  if (!response.ok) throw new Error('Place search failed');

  const json = await response.json();

  return (json.features || [])
    .filter(
      (feature) =>
        Array.isArray(feature.center) &&
        Number.isFinite(feature.center[0]) &&
        Number.isFinite(feature.center[1])
    )
    .map((feature) => ({
      id: feature.id || feature.place_name,
      name: feature.place_name || feature.text || '',
      // [lng, lat] — the order the geocoder returns and the order the API,
      // the model and the 2dsphere index all store.
      coordinates: [feature.center[0], feature.center[1]],
    }));
};

/**
 * Name a point.
 *
 * Hotspots come back from the server as bare grid coordinates on purpose — the
 * server does no geocoding and holds no Mapbox credential — so the label
 * ("New York", "Tel Aviv-Yafo") is resolved here, where the token already
 * lives. `place` and `region` only: the point is the centre of a ~55 km cell,
 * so asking for a neighbourhood or a POI would attach a spuriously precise
 * name to a deliberately coarse location.
 *
 * Resolves to null rather than throwing when the lookup is unavailable or
 * finds nothing — an unlabelled hotspot is still a usable destination, so the
 * caller should fall back to coordinates rather than hide the row.
 */
export const reverseGeocode = async (coordinates) => {
  const usable =
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    coordinates.every((n) => Number.isFinite(n));
  if (!usable || !isPlaceSearchAvailable()) return null;

  const [lng, lat] = coordinates;
  try {
    const response = await fetch(
      `${GEOCODER}/${lng},${lat}.json` +
        `?access_token=${token()}&types=place,region&limit=1`
    );
    if (!response.ok) return null;
    const json = await response.json();
    const feature = (json.features || [])[0];
    if (!feature) return null;
    // `text` is the bare place name; `place_name` appends region and country,
    // which is too long for a chip but is the better fallback of the two.
    return feature.text || feature.place_name || null;
  } catch {
    return null;
  }
};
